/**
 * =============================================================================
 * QUESTION ACTIONS COMPONENT
 * =============================================================================
 *
 * Action buttons (View, Approve, Reject) for question rows.
 *
 * EXTRACTED FROM: QuestionQueue.tsx (P1-17)
 *
 * =============================================================================
 */

import React, { useState } from 'react';
import { Button, Flex, FlexItem } from '@bigcommerce/big-design';
import { CheckIcon, CloseIcon, VisibilityIcon } from '@bigcommerce/big-design-icons';
import type { AdminQuestionListItem } from '@find-my-part/types';

/**
 * Props for QuestionActions component.
 */
export interface QuestionActionsProps {
  /** The question to display actions for */
  question: AdminQuestionListItem;
  /** Callback when approve button is clicked */
  onApprove: (questionId: string) => Promise<boolean | void>;
  /** Callback when reject button is clicked */
  onReject: (questionId: string) => Promise<void>;
  /** Callback when view button is clicked */
  onView: (question: AdminQuestionListItem) => void;
  /** P1-8: Set of question IDs with pending actions (for disabling buttons) */
  pendingActions?: Set<string>;
  /** Keep the queue context stable while a status outcome awaits recovery. */
  navigationDisabled?: boolean;
  /** Optional test ID prefix */
  testId?: string;
}

/**
 * Action buttons component for question rows.
 *
 * Displays View, Approve, and Reject buttons with proper loading states.
 * Approve/Reject buttons only appear for PENDING_REVIEW questions.
 *
 * @example
 * ```tsx
 * <QuestionActions
 *   question={question}
 *   onApprove={handleApprove}
 *   onReject={handleReject}
 *   onView={handleView}
 *   testId="queue"
 * />
 * ```
 */
export function QuestionActions({
  question,
  onApprove,
  onReject,
  onView,
  pendingActions,
  navigationDisabled = false,
  testId,
}: QuestionActionsProps): React.ReactElement {
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);

  // P1-8: Include parent-level pending state to prevent concurrent actions
  const isPendingFromParent = pendingActions?.has(question.questionId) ?? false;
  const isLoading = isApproving || isRejecting || isPendingFromParent;
  // Keep the controls mounted (disabled/loading) while the optimistic parent
  // status is already showing its target. Unmounting the focused action during
  // the request drops keyboard/SR focus before an outcome can be announced.
  const canModerate = question.status === 'PENDING_REVIEW' || isPendingFromParent;

  const handleApprove = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!canModerate || isLoading) return;
    setIsApproving(true);
    try {
      await onApprove(question.questionId);
    } finally {
      setIsApproving(false);
    }
  };

  const handleReject = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!canModerate || isLoading) return;
    setIsRejecting(true);
    try {
      await onReject(question.questionId);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleView = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (navigationDisabled) return;
    onView(question);
  };

  return (
    <Flex flexGap="0.5rem" alignItems="center">
      <FlexItem>
        <Button
          variant="subtle"
          onClick={handleView}
          disabled={navigationDisabled}
          iconLeft={<VisibilityIcon />}
          data-question-workspace-trigger={question.questionId}
          data-testid={testId ? `${testId}-row-${question.questionId}-view-btn` : undefined}
        >
          View
        </Button>
      </FlexItem>
      {canModerate && (
        <>
          <FlexItem>
            <Button
              variant="primary"
              onClick={handleApprove}
              disabled={isLoading}
              isLoading={isApproving}
              iconLeft={!isApproving ? <CheckIcon /> : undefined}
              data-testid={testId ? `${testId}-row-${question.questionId}-approve-btn` : undefined}
            >
              Approve
            </Button>
          </FlexItem>
          <FlexItem>
            <Button
              variant="secondary"
              actionType="destructive"
              onClick={handleReject}
              disabled={isLoading}
              isLoading={isRejecting}
              iconLeft={!isRejecting ? <CloseIcon /> : undefined}
              data-testid={testId ? `${testId}-row-${question.questionId}-reject-btn` : undefined}
            >
              Reject
            </Button>
          </FlexItem>
        </>
      )}
    </Flex>
  );
}
